import legacyDocument from '../legacy/game-wiki/index.html?raw';
import communityDocument from '../legacy/game-wiki/community.html?raw';
import { renderHomeNavigationPanels } from '../components/home-navigation-panels';
import { renderHomePromotionSlots } from '../components/home-promotion-slots';
import type { CatalogNavigationItem } from './catalog-navigation';
import type { HeroDirectoryEntry } from './heroes/directory-types';
import { heroArchiveUrl } from './heroes/routes';
import { rewriteStaticUrlsInHtml, staticUrl } from './config/static-assets';
import { handleImgToWebp, rewriteHtmlImgToWebp } from './config/image-webp';
import type { HomepageConfig } from './homepage-config';
import type { CatalogUpdate } from './catalog-updates';

const bodyMatch = legacyDocument.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
const communityMatch = communityDocument.match(
  /<main class="community-layout">([\s\S]*?)<\/main>/i,
);
const communityDialogMatch = communityDocument.match(
  /<dialog id="community-dialog"[\s\S]*?<\/dialog>/i,
);

const baseMarkup = (bodyMatch?.[1] ?? '')
  .replace(/<script[\s\S]*?<\/script>/gi, '')
  .replace(/<header class="site-header"[\s\S]*?<\/header>/i, '')
  .replace(/<section class="hero"[\s\S]*?<\/section>/i, '')
  .replace(/<nav class="category-nav"[\s\S]*?<\/nav>/i, '')
  .replace(/<dialog id="wiki-quick-dialog"[\s\S]*?<\/dialog>/i, '')
  .replace(
    /<footer class="site-footer">[\s\S]*?<\/footer>/i,
    '<div data-shared-footer-anchor></div>',
  )
  .replace(
    /<aside class="guide-authors"[\s\S]*?<\/aside>/i,
    '<aside class="guide-authors guide-albums-sidebar" aria-label="攻略专辑"></aside>',
  )
  .replace('<!-- HOME_NAVIGATION_PANELS -->', renderHomeNavigationPanels('arcanatactics'))
  .replace('<!-- HOME_PROMOTION_SLOTS -->', renderHomePromotionSlots('arcanatactics'))
  .replaceAll('./assets/', `${staticUrl('/legacy-game-wiki/assets/')}`)
  .replaceAll('src="assets/', `src="${staticUrl('/legacy-game-wiki/assets/')}`)
  .replaceAll('href="./community.html"', 'href="/arcanatactics/dcommunity"')
  .replaceAll('href="community.html"', 'href="/arcanatactics/dcommunity"')
  .replaceAll('href="./profile.html"', 'href="/arcanatactics/profile"')
  .replaceAll(
    'href="https://www.gamekee.com/arcanatactics/684719.html"',
    'href="/arcanatactics/684719"',
  )
  .replaceAll(
    'href="https://www.gamekee.com/arcanatactics/682872.html"',
    'href="/arcanatactics/682872"',
  )
  .replaceAll(
    'href="https://www.gamekee.com/arcanatactics/719595.html"',
    'href="/arcanatactics/719595"',
  )
  .replaceAll(
    'href="https://www.gamekee.com/arcanatactics/718927.html"',
    'href="/arcanatactics/718927"',
  )
  .replaceAll('href="#catalog"', 'href="/arcanatactics/dcatalog"')
  .replaceAll('href="#guides"', 'href="/arcanatactics/dguides"')
  .replaceAll('href="#tools"', 'href="/arcanatactics/dtools"')
  .replaceAll('href="#"', 'href="/arcanatactics"')
  .replace(
    /http:\/\/localhost:3002\/heroes\/arcanatactics\/(\d+)/g,
    '/arcanatactics/t$1',
  );

export type LegacyView = 'home' | 'catalog' | 'guides' | 'tools' | 'benefits' | 'community';

function escapeMarkup(value: string | number) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

export function renderCatalogCards(heroes: HeroDirectoryEntry[], _gameKey: string) {
  return heroes
    .map((hero) => {
      const name = escapeMarkup(hero.name);
      const id = escapeMarkup(hero.id);
      const grade = escapeMarkup(hero.facets.stars);
      const job = escapeMarkup(hero.facets.category);
      const element = escapeMarkup(hero.facets.attribute);
      const image = escapeMarkup(handleImgToWebp(hero.head));
      const href = escapeMarkup(heroArchiveUrl(hero, heroes));
      return `<a class="catalog-card" href="${href}" data-local-hero="${id}" data-name="${name} ${id}" data-grade="${grade}" data-job="${job}" data-element="${element}"><div class="catalog-art"><img src="${image}" alt="${name}卡面" loading="lazy" width="160" height="220"></div><div class="catalog-card-info"><h2>${name}</h2></div></a>`;
    })
    .join('');
}

export function renderFeaturedHeroCards(heroes: HeroDirectoryEntry[], _gameKey: string) {
  return [...heroes]
    .sort((left, right) => {
      const date = (right.updatedAt || '').localeCompare(left.updatedAt || '');
      return date || Number(right.id) - Number(left.id);
    })
    .slice(0, 8)
    .map((hero) => {
      const name = escapeMarkup(hero.name);
      const id = escapeMarkup(hero.id);
      const grade = escapeMarkup(hero.facets.stars);
      const job = escapeMarkup(hero.facets.category);
      const element = escapeMarkup(hero.facets.attribute);
      const image = escapeMarkup(handleImgToWebp(hero.head));
      const updatedAt = /^\d{4}-\d{2}-\d{2}/.exec(hero.updatedAt || '')?.[0];
      const dateLabel = updatedAt
        ? `${updatedAt.slice(5).replace('-', '.')} 更新`
        : '图鉴已收录';
      const href = escapeMarkup(heroArchiveUrl(hero, heroes));
      return `<a class="catalog-card featured-hero-card" href="${href}" data-local-hero="${id}" data-name="${name} ${id}" data-grade="${grade}" data-job="${job}" data-element="${element}"><div class="catalog-art"><img src="${image}" alt="${name}卡面" loading="lazy" width="160" height="220"></div><div class="catalog-card-info"><h2>${name}</h2><time class="featured-hero-date"${updatedAt ? ` datetime="${updatedAt}"` : ''}>${dateLabel}</time></div></a>`;
    })
    .join('');
}

function renderCatalogUpdateCards(updates: CatalogUpdate[]) {
  return updates.map((item) => {
    const name = escapeMarkup(item.name);
    const image = escapeMarkup(handleImgToWebp(item.image));
    const updatedAt = /^\d{4}-\d{2}-\d{2}/.exec(item.updatedAt)?.[0];
    const dateLabel = updatedAt ? `${updatedAt.slice(5).replace('-', '.')} 更新` : '图鉴已收录';
    const href = escapeMarkup(item.href);
    const catalogName = escapeMarkup(item.catalogName);
    return `<a class="catalog-card featured-hero-card" data-catalog-key="${escapeMarkup(item.catalogKey)}" href="${href}"><div class="catalog-art"><div class="catalog-art-window"><img src="${image}" alt="${name}卡面" loading="lazy" width="160" height="220"></div></div><div class="catalog-card-info"><h2>${name}</h2><span class="featured-hero-catalog">${catalogName}</span><time class="featured-hero-date"${updatedAt ? ` datetime="${updatedAt}"` : ''}>${dateLabel}</time></div></a>`;
  }).join('') || '<p class="featured-hero-empty">暂无图鉴更新</p>';
}

export function updateFeaturedCatalogCards(root: Pick<ParentNode, 'querySelector'>, updates: CatalogUpdate[]) {
  const featured = root.querySelector('.featured-hero-grid');
  if (featured) featured.innerHTML = renderCatalogUpdateCards(updates);
}

export function renderCatalogNavigation(
  catalogs: CatalogNavigationItem[],
  heroCount: number,
  gameDomain: string,
) {
  return `<nav class="collection-entries" aria-label="专题图鉴">${catalogs
    .map((catalog, index) => {
      const key = escapeMarkup(catalog.catalogKey);
      const name = escapeMarkup(catalog.name);
      const mode = escapeMarkup(catalog.displayMode);
      const meta =
        catalog.catalogKey === 'hero'
          ? `${heroCount} 位英雄`
          : catalog.displayMode === 'modal'
            ? '弹窗页面'
            : '独立页面';
      const image =
        catalog.iconUrl ||
        staticUrl(
          `/legacy-game-wiki/assets/home-tiles/${
            catalog.catalogKey === 'hero'
              ? 'catalog-heroes'
              : /卡牌/.test(catalog.name)
                ? 'catalog-cards'
                : 'catalog-equipment'
          }.jpg`,
        );
      return `<a class="collection-entry${index === 0 ? ' is-active' : ''}" href="/${encodeURIComponent(gameDomain)}/dcatalog/${encodeURIComponent(catalog.catalogKey)}" data-collection="${key}" data-display-mode="${mode}"${index === 0 ? ' aria-current="page"' : ''}><img src="${escapeMarkup(image)}" alt=""><span class="collection-copy"><strong>${name}</strong><em>${meta}</em></span><span class="collection-arrow" aria-hidden="true">›</span></a>`;
    })
    .join('')}</nav>`;
}

/** 拼装游戏壳 HTML；可在服务端（bot SSR）或浏览器（用户侧 bootstrap）调用。 */
export function buildLegacyShellMarkup(
  view: LegacyView,
  heroDirectory: HeroDirectoryEntry[],
  gameKey: string,
  catalogNavigation: CatalogNavigationItem[],
  homepageConfig: HomepageConfig | null,
  catalogUpdates?: CatalogUpdate[],
) {
  let markup = baseMarkup
    .replace(/<div class="catalog-heading">[\s\S]*?<\/div><\/div>/, '')
    .replace(/<div class="hero-library-heading">[\s\S]*?<\/div>/, '')
    .replaceAll('/arcanatactics', `/${gameKey}`)
    .replace(
      '<section class="content-shell" aria-label="Wiki 内容概览">',
      `<section class="content-shell"${view === 'home' ? '' : ' hidden'} aria-label="Wiki 内容概览">`,
    )
    .replace(
      '<section class="catalog-layout" hidden',
      `<section class="catalog-layout"${view === 'catalog' ? '' : ' hidden'}`,
    )
    .replace(
      '<section class="guides-layout" hidden',
      `<section class="guides-layout"${view === 'guides' ? '' : ' hidden'}`,
    )
    .replace(
      '<section class="tools-layout" hidden',
      `<section class="tools-layout"${view === 'tools' ? '' : ' hidden'}`,
    )
    .replace(
      '<section class="benefits-layout" hidden',
      `<section class="benefits-layout"${view === 'benefits' ? '' : ' hidden'}`,
    )
    .replace(
      /<nav class="collection-entries"[\s\S]*?<\/nav>/,
      renderCatalogNavigation(catalogNavigation, heroDirectory.length, gameKey),
    )
    // 先清空 index.html 里 arcanatactics 写死的默认攻略，有配置时再按 guideLinks 回填
    .replace(
      /(<nav class="dungeon-guide-grid">)[\s\S]*?(<\/nav>)/,
      '$1$2',
    )
    .replace(
      /<div class="hero-library-heading"><h2>英雄图鉴 <span>· \d+<\/span><\/h2><\/div>/,
      `<div class="hero-library-heading"><h2>英雄图鉴 <span>· ${heroDirectory.length}</span></h2></div>`,
    )
    .replace(/<span>UP 英雄<\/span>/, '<span>图鉴更新</span>')
    .replace(
      /<div class="featured-hero-grid">[\s\S]*?<\/div>\s*<\/section>\s*<section class="section-block version-section"/,
      `<div class="featured-hero-grid">${catalogUpdates ? renderCatalogUpdateCards(catalogUpdates) : renderFeaturedHeroCards(heroDirectory, gameKey)}</div>\n          </section>\n\n          <section class="section-block version-section"`,
    )
    .replace(
      /<div class="catalog-grid">[\s\S]*?<\/div><p id="catalog-empty"/,
      `<div class="catalog-grid">${renderCatalogCards(heroDirectory, gameKey)}</div><p id="catalog-empty"`,
    )
    .replace(
      /<span id="catalog-count" aria-live="polite">共 \d+ 位英雄<\/span>/,
      `<span id="catalog-count" aria-live="polite">共 ${heroDirectory.length} 位英雄</span>`,
    );
  if (homepageConfig) {
    const guide = homepageConfig.guideLinks[0] || homepageConfig.featuredGuide;
    const versionStart = markup.indexOf('<section class="section-block version-section"');
    const versionEnd = markup.indexOf(
      '<section class="section-block home-catalog-section',
      versionStart,
    );
    if (versionStart >= 0 && versionEnd > versionStart) {
      const original = markup.slice(versionStart, versionEnd);
      const cover = handleImgToWebp(
        guide.image || homepageConfig.featuredGuide.image,
      ).replaceAll('"', '%22');
      const guideCount = Math.max(homepageConfig.guideLinks.length, 1);
      const version = original
        .replace(
          /(<div class="section-title"><span>)[^<]*/,
          (_match, prefix: string) => `${prefix}${escapeMarkup(homepageConfig.versionTitle)}`,
        )
        .replace(
          /<div class="version-grid">\s*<a[^>]*>[\s\S]*?<\/a>/,
          `<div class="version-grid"><a href="${escapeMarkup(guide.url || `/${gameKey}/dguides`)}" style="--homepage-version-cover:url(&quot;${escapeMarkup(cover)}&quot;)"><h2>${escapeMarkup(guide.title)}</h2><p class="version-guide-description">${escapeMarkup('description' in guide ? guide.description : '')}</p><span class="entry-action">查看全部攻略</span></a>`,
        )
        .replace(
          /(<nav class="dungeon-guide-grid">)[\s\S]*?(<\/nav>)/,
          (_match, open: string, close: string) =>
            `${open.replace(
              'class="dungeon-guide-grid"',
              `class="dungeon-guide-grid" style="grid-template-rows:repeat(${guideCount},minmax(0,1fr))"`,
            )}${homepageConfig.guideLinks
              .slice(0, 6)
              .map(
                (item, index) =>
                  `<a href="${escapeMarkup(item.url || `/${gameKey}/dguides`)}"${index === 0 ? ' class="is-active" aria-current="true"' : ''}><b>${escapeMarkup(item.title)}</b></a>`,
              )
              .join('')}${close}`,
        );
      markup = markup.slice(0, versionStart) + version + markup.slice(versionEnd);
    }
    const now = new Date();
    const dateLabel = new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }).format(now);
    markup = markup.replace(
      '<time id="current-date">正在读取日期</time>',
      `<time id="current-date">${escapeMarkup(dateLabel)}</time>`,
    );
    const launch = homepageConfig.launchDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (launch) {
      const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      const start = Date.UTC(Number(launch[1]), Number(launch[2]) - 1, Number(launch[3]));
      markup = markup.replace(
        '<strong id="server-days">--</strong>',
        `<strong id="server-days">${Math.max(0, Math.floor((today - start) / 86400000) + 1)}</strong>`,
      );
    }
  }
  if (communityMatch) {
    const communityBody = communityMatch[1]
      .replaceAll('src="assets/', `src="${staticUrl('/legacy-game-wiki/assets/')}`)
      .replaceAll(
        'data-image="assets/',
        `data-image="${staticUrl('/legacy-game-wiki/assets/')}`,
      );
    const communityMarkup = `<main class="community-layout embedded-community"${view === 'community' ? '' : ' hidden'}>${communityBody}</main>`;
    markup = markup.replace(
      '<div data-shared-footer-anchor></div>',
      `${communityMarkup}<div data-shared-footer-anchor></div>`,
    );
  }
  if (communityDialogMatch)
    markup += communityDialogMatch[0]
      .replaceAll('src="assets/', `src="${staticUrl('/legacy-game-wiki/assets/')}`)
      .replaceAll(
        'data-image="assets/',
        `data-image="${staticUrl('/legacy-game-wiki/assets/')}`,
      );
  return rewriteHtmlImgToWebp(
    rewriteStaticUrlsInHtml(
      markup
        .replaceAll(
          'href="#community-announcements"',
          `href="/${gameKey}/dcommunity#community-announcements"`,
        )
        .replaceAll('/arcanatactics', `/${gameKey}`),
    ),
  );
}

/**
 * 在已挂载的壳 DOM 上填充图鉴等动态区块，避免整页替换成「加载中」。
 */
export function applyLegacyShellDynamicContent(
  root: Pick<ParentNode, 'querySelector'>,
  {
    gameDomain,
    heroDirectory,
    catalogNavigation,
    catalogUpdates,
  }: {
    gameDomain: string;
    heroDirectory: HeroDirectoryEntry[];
    catalogNavigation: CatalogNavigationItem[];
    catalogUpdates?: CatalogUpdate[];
  },
) {
  const catalogGrid = root.querySelector('.catalog-grid');
  if (catalogGrid) {
    catalogGrid.innerHTML = renderCatalogCards(heroDirectory, gameDomain);
  }
  const featured = root.querySelector('.featured-hero-grid');
  if (featured) {
    featured.innerHTML = catalogUpdates ? renderCatalogUpdateCards(catalogUpdates) : renderFeaturedHeroCards(heroDirectory, gameDomain);
  }
  const nav = root.querySelector('nav.collection-entries');
  if (nav) {
    nav.outerHTML = renderCatalogNavigation(
      catalogNavigation,
      heroDirectory.length,
      gameDomain,
    );
  }
  const count = root.querySelector('#catalog-count');
  if (count) {
    count.textContent = `共 ${heroDirectory.length} 位英雄`;
  }
  const heading = root.querySelector('.hero-library-heading h2 span');
  if (heading) {
    heading.textContent = `· ${heroDirectory.length}`;
  }
}
